/**
 * data.gov.il fetchers for auto-services:
 *   - gas stations (with lat/lon)
 *   - garages + vehicle-inspection centres (shared dataset, split by type)
 *
 * Returns normalised records — callers don't need to know the Hebrew field
 * names from the underlying CKAN tables.
 *
 * @typedef {'gas' | 'garage' | 'inspection'} GovKind
 *
 * @typedef {Object} GovService
 * @property {string}  id            stable id (`${kind}-${row._id}`)
 * @property {string}  name          display name
 * @property {GovKind} kind
 * @property {string}  [brand]       chain/brand (gas only)
 * @property {string}  [address]
 * @property {string}  [city]
 * @property {string}  [phone]
 * @property {number}  [lat]         WGS84 — gas only
 * @property {number}  [lon]
 * @property {string}  [profession]  miktzoa (garage only — what they service)
 */

import { Ckan } from '../../app/config.js';
import * as ckan from './ckan.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('govServices');

const GAS_RESOURCE = Ckan.resources.gasStations;
const GAR_RESOURCE = Ckan.resources.garagesAndInspections;

// Exact `sug_mosah` values seen on the dataset. We pass them to the CKAN
// server as filters (much faster than pulling everything and filtering
// client-side, especially since there are 12,776 "מוסך מורשה" rows in the
// country). Keep these in sync with whatever the source data uses.
const GARAGE_SUG_MOSAH = 'מוסך מורשה';
const INSPECTION_SUG_MOSAH = 'מכון רישוי';

/**
 * Fetch all public gas stations. The dataset is small (~1k rows) so we pull
 * the whole list and let the caller filter by distance client-side.
 *
 * @returns {Promise<GovService[]>}
 */
export async function fetchGasStations() {
  /** @type {any[]} */
  const rows = await ckan.search({
    resourceId: GAS_RESOURCE,
    limit: 5000,
  });
  /** @type {GovService[]} */
  const out = [];
  for (const r of rows) {
    const lat = parseNum(r['נ.צ. רוחב']);
    const lon = parseNum(r['נ.צ. אורך']);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue; // skip unmappable
    out.push({
      id: `gas-${r._id}`,
      name: clean(r['שם_תחנה']) || clean(r['חברה']) || 'תחנת דלק',
      kind: 'gas',
      brand: clean(r['חברה']) || undefined,
      address: clean(r['כתובת']) || undefined,
      city: clean(r['רשות_מקומית']) || undefined,
      lat, lon,
    });
  }
  log.info('fetched gas stations:', out.length);
  return out;
}

/**
 * Fetch garages, optionally filtered by city. Result has no lat/lon — sort
 * by name. The dataset uses an inconsistent yishuv format (e.g. Tel Aviv is
 * stored as `"תל אביב -יפו"` with an unusual space-before-dash) so we use
 * full-text search (CKAN `q`) instead of strict equality.
 *
 * @param {{ city?: string }} [opts]
 * @returns {Promise<GovService[]>}
 */
export async function fetchGarages({ city } = {}) {
  return fetchFromGaragesDataset({ city, sugMosah: GARAGE_SUG_MOSAH, kind: 'garage' });
}

/**
 * Fetch vehicle inspection centres (מכוני רישוי), optionally filtered by city.
 *
 * @param {{ city?: string }} [opts]
 * @returns {Promise<GovService[]>}
 */
export async function fetchInspectionCenters({ city } = {}) {
  return fetchFromGaragesDataset({ city, sugMosah: INSPECTION_SUG_MOSAH, kind: 'inspection' });
}

// ====================================================================
// internals
// ====================================================================

/**
 * @param {{ city?: string; sugMosah: string; kind: 'garage' | 'inspection' }} args
 * @returns {Promise<GovService[]>}
 */
async function fetchFromGaragesDataset({ city, sugMosah, kind }) {
  /** @type {any[]} */
  const rows = await ckan.search({
    resourceId: GAR_RESOURCE,
    // Server-side: narrow to the requested facility type (server is fast at
    // this — 12,776 rows for "מוסך מורשה" countrywide).
    filters: { sug_mosah: sugMosah },
    // Server-side full-text — handles `"תל אביב"` finding `"תל אביב -יפו"`
    // and similar yishuv-spelling drift. If no city supplied we don't add it.
    q: city || undefined,
    // Cap response size — a single city has at most a few hundred entries.
    limit: 500,
  });
  /** @type {GovService[]} */
  const out = [];
  for (const r of rows) {
    out.push({
      id: `${kind}-${r._id}`,
      name: clean(r.shem_mosah) || sugMosah,
      kind,
      address: clean(r.ktovet) || undefined,
      city: clean(r.yishuv) || undefined,
      phone: cleanPhone(r.telephone),
      profession: clean(r.miktzoa) || undefined,
    });
  }
  log.info(`fetched ${kind}s:`, out.length, city ? `(city=${city})` : '(all)');
  return out;
}

/** @param {unknown} v */
function parseNum(v) {
  if (v == null) return NaN;
  const n = Number(String(v).replace(/,/g, '.'));
  return Number.isFinite(n) ? n : NaN;
}

/** @param {unknown} v */
function clean(v) {
  return v == null ? '' : String(v).trim();
}

/** @param {unknown} v */
function cleanPhone(v) {
  const s = clean(v).replace(/[^\d+]/g, '');
  return s.length >= 7 ? s : undefined;
}
